export interface Ministry {
  id: string;
  title: string;
  icon: string;
  schedule: string;
  location: string;
  description: string;
  extendedDescription?: string;
  features: string[];
  image: string;
  heroImage?: string;
  subtitle?: string;
  gallery?: string[];
  testimonials?: {
    quote: string;
    name: string;
    role: string;
  }[];
  leadership?: {
    name: string;
    title: string;
    bio: string;
    contact?: string;
  };
  scheduleDetails?: {
    day: string;
    time: string;
    activity: string;
    location: string;
  }[];
  quickInfo?: string[];
}

export const ministries: Ministry[] = [
  {
    id: 'bible-study',
    title: 'Bible Study',
    icon: 'ri-book-open-line',
    schedule: 'Wednesdays, 7:00 PM',
    location: 'Church Hall',
    description: 'Join us for in-depth study of Scripture, exploring the Word of God together in a supportive community environment.',
    extendedDescription: 'Our Bible Study sessions provide a welcoming space for believers of all levels to deepen their understanding of Scripture. Through guided discussion, prayer, and fellowship, we explore both Old and New Testament passages, examining their historical context and modern application. Whether you\'re new to the faith or have been studying Scripture for years, you\'ll find a community eager to learn and grow together.',
    features: [
      'Weekly Scripture exploration',
      'Group discussions and Q&A',
      'Study materials provided',
      'Prayer and fellowship time',
      'All ages welcome',
      'Refreshments served'
    ],
    image: '/assets/readdy/coptic-church-interior-001-fdff2518bcad.jpg',
    gallery: [
      '/assets/readdy/coptic-church-interior-001-fdff2518bcad.jpg',
      '/assets/readdy/coptic-church-interior-001-fdff2518bcad.jpg',
      '/assets/readdy/coptic-church-interior-001-fdff2518bcad.jpg',
      '/assets/readdy/coptic-church-interior-001-fdff2518bcad.jpg',
      '/assets/readdy/coptic-church-interior-001-fdff2518bcad.jpg',
      '/assets/readdy/coptic-church-interior-001-fdff2518bcad.jpg'
    ],
    testimonials: [
      {
        quote: 'Bible Study has transformed my understanding of Scripture. The discussions are enriching and the fellowship is wonderful. I look forward to it every week!',
        name: 'Mary Girgis',
        role: 'Member since 2020'
      },
      {
        quote: 'As a new believer, I was nervous about joining, but everyone was so welcoming. I\'ve learned so much and made lifelong friends.',
        name: 'John Mikhail',
        role: 'Member since 2022'
      }
    ],
    leadership: {
      name: 'Deacon Peter Salib',
      title: 'Bible Study Coordinator',
      bio: 'Deacon Peter has been teaching Scripture for over 15 years and holds a degree in Theology. His passion for God\'s Word and gift for teaching make each session both educational and inspiring.',
      contact: 'biblestudy@church.org'
    },
    scheduleDetails: [
      {
        day: 'Wednesday',
        time: '7:00 PM - 8:30 PM',
        activity: 'Main Bible Study Session',
        location: 'Church Hall'
      },
      {
        day: 'Wednesday',
        time: '8:30 PM - 9:00 PM',
        activity: 'Fellowship & Refreshments',
        location: 'Church Hall'
      }
    ],
    quickInfo: [
      'No prior knowledge required',
      'Study materials provided',
      'Childcare available',
      'Free parking'
    ]
  },
  {
    id: 'hymns-class',
    title: 'Hymns Class',
    icon: 'ri-music-2-line',
    schedule: 'Fridays, 6:00 PM',
    location: 'Sanctuary',
    description: 'Learn and practice traditional Coptic hymns, preserving our rich musical heritage and deepening your worship experience.',
    extendedDescription: 'The Coptic Orthodox Church has a rich tradition of sacred hymns dating back centuries. Our Hymns Class offers instruction in both Coptic and Arabic hymns, teaching proper pronunciation, melody, and the spiritual significance of each hymn. Students learn to read musical notation and understand the liturgical context of various hymns used throughout the church year.',
    features: [
      'Traditional Coptic hymns',
      'Arabic hymns instruction',
      'Musical notation training',
      'Liturgical context education',
      'Performance opportunities',
      'All skill levels welcome'
    ],
    image: '/assets/readdy/coptic-church-interior-001-fdff2518bcad.jpg',
    gallery: [
      '/assets/readdy/coptic-church-interior-001-fdff2518bcad.jpg',
      '/assets/readdy/coptic-church-interior-001-fdff2518bcad.jpg',
      '/assets/readdy/coptic-church-interior-001-fdff2518bcad.jpg',
      '/assets/readdy/coptic-church-interior-001-fdff2518bcad.jpg',
      '/assets/readdy/coptic-church-interior-001-fdff2518bcad.jpg',
      '/assets/readdy/coptic-church-interior-001-fdff2518bcad.jpg'
    ],
    testimonials: [
      {
        quote: 'Learning our traditional hymns has connected me to my heritage in a profound way. The beauty of these ancient melodies brings me closer to God.',
        name: 'Marina Youssef',
        role: 'Member since 2019'
      },
      {
        quote: 'I never thought I could learn to sing in Coptic, but the instructors are patient and encouraging. Now I can participate fully in the liturgy!',
        name: 'David Nabil',
        role: 'Member since 2021'
      }
    ],
    leadership: {
      name: 'Cantor Mina Aziz',
      title: 'Hymns Class Director',
      bio: 'Cantor Mina has dedicated his life to preserving and teaching Coptic hymns. Trained in both Egypt and abroad, he brings decades of experience and a deep love for our musical tradition.',
      contact: 'hymns@church.org'
    },
    scheduleDetails: [
      {
        day: 'Friday',
        time: '6:00 PM - 7:00 PM',
        activity: 'Beginner Hymns Class',
        location: 'Sanctuary'
      },
      {
        day: 'Friday',
        time: '7:00 PM - 8:30 PM',
        activity: 'Advanced Hymns & Choir Practice',
        location: 'Sanctuary'
      }
    ],
    quickInfo: [
      'No musical experience needed',
      'Hymn books provided',
      'Audio recordings available',
      'Join the choir'
    ]
  },
  {
    id: 'sunday-school',
    title: 'Sunday School',
    icon: 'ri-graduation-cap-line',
    schedule: 'Sundays, 9:00 AM',
    location: 'Education Wing',
    description: 'Comprehensive faith education for children and youth, teaching Biblical principles and Coptic Orthodox traditions in an engaging environment.',
    extendedDescription: 'Our Sunday School program provides age-appropriate Christian education for children from preschool through high school. Using interactive lessons, crafts, games, and multimedia resources, we teach Biblical stories, church history, Coptic traditions, and moral values. Our dedicated teachers create a nurturing environment where young people can ask questions, build friendships, and develop a strong foundation in their faith.',
    features: [
      'Age-appropriate curriculum',
      'Interactive lessons & activities',
      'Biblical stories & teachings',
      'Coptic Orthodox traditions',
      'Character development',
      'Qualified teachers'
    ],
    image: '/assets/readdy/coptic-church-interior-001-fdff2518bcad.jpg',
    gallery: [
      '/assets/readdy/coptic-church-interior-001-fdff2518bcad.jpg',
      '/assets/readdy/coptic-church-interior-001-fdff2518bcad.jpg',
      '/assets/readdy/events-main-001-9e5bec3e8310.jpg',
      '/assets/readdy/coptic-church-interior-001-fdff2518bcad.jpg',
      '/assets/readdy/events-main-001-9e5bec3e8310.jpg',
      '/assets/readdy/coptic-church-interior-001-fdff2518bcad.jpg'
    ],
    testimonials: [
      {
        quote: 'My children love Sunday School! They come home excited to share what they learned. The teachers truly care about each child\'s spiritual growth.',
        name: 'Sarah Hanna',
        role: 'Parent'
      },
      {
        quote: 'Sunday School gave me a strong foundation in my faith. The lessons I learned as a child still guide me today as a young adult.',
        name: 'Christine Fawzy',
        role: 'Former Student, Now Volunteer'
      }
    ],
    leadership: {
      name: 'Tasoni Mariam Kamal',
      title: 'Sunday School Director',
      bio: 'Tasoni Mariam has over 20 years of experience in Christian education. Her creative teaching methods and genuine love for children make Sunday School a highlight of every child\'s week.',
      contact: 'sundayschool@church.org'
    },
    scheduleDetails: [
      {
        day: 'Sunday',
        time: '9:00 AM - 9:45 AM',
        activity: 'Preschool Class (Ages 3-5)',
        location: 'Room 101'
      },
      {
        day: 'Sunday',
        time: '9:00 AM - 10:00 AM',
        activity: 'Elementary Class (Ages 6-11)',
        location: 'Room 102'
      },
      {
        day: 'Sunday',
        time: '9:00 AM - 10:15 AM',
        activity: 'Middle School Class (Ages 12-14)',
        location: 'Room 103'
      },
      {
        day: 'Sunday',
        time: '9:00 AM - 10:15 AM',
        activity: 'High School Class (Ages 15-18)',
        location: 'Room 104'
      }
    ],
    quickInfo: [
      'Ages 3-18 welcome',
      'Safe & secure environment',
      'Snacks provided',
      'Parent volunteers needed'
    ]
  },
  {
    id: 'youth-meeting',
    title: 'Youth Meeting',
    icon: 'ri-team-line',
    schedule: 'Saturdays, 5:00 PM',
    location: 'Youth Center',
    description: 'Dynamic fellowship for young adults featuring discussions, activities, and spiritual growth opportunities in a contemporary setting.',
    extendedDescription: 'Youth Meeting provides a vibrant community for young adults (ages 18-35) to connect, grow in faith, and build lasting friendships. Our gatherings include contemporary worship, relevant Biblical teaching, small group discussions, service projects, and social activities. We address real-life challenges facing young adults today—career decisions, relationships, identity, and purpose—all through the lens of Orthodox Christian faith.',
    features: [
      'Contemporary worship',
      'Relevant Biblical teaching',
      'Small group discussions',
      'Service projects',
      'Social activities & outings',
      'Mentorship opportunities'
    ],
    image: '/assets/readdy/events-main-001-9e5bec3e8310.jpg',
    gallery: [
      '/assets/readdy/events-main-001-9e5bec3e8310.jpg',
      '/assets/readdy/events-main-001-9e5bec3e8310.jpg',
      '/assets/readdy/events-main-001-9e5bec3e8310.jpg',
      '/assets/readdy/coptic-church-interior-001-fdff2518bcad.jpg',
      '/assets/readdy/events-main-001-9e5bec3e8310.jpg',
      '/assets/readdy/events-main-001-9e5bec3e8310.jpg'
    ],
    testimonials: [
      {
        quote: 'Youth Meeting is where I found my church family. The friendships I\'ve made here and the spiritual growth I\'ve experienced have been life-changing.',
        name: 'Andrew Samir',
        role: 'Member since 2020'
      },
      {
        quote: 'I love that we can discuss real issues and tough questions in a safe, judgment-free environment. This group has strengthened my faith immensely.',
        name: 'Jessica Mina',
        role: 'Member since 2021'
      }
    ],
    leadership: {
      name: 'Abouna Bishoy Marcos',
      title: 'Youth Ministry Leader',
      bio: 'Father Bishoy has a heart for young people and understands the unique challenges they face. His relatable teaching style and genuine care create a welcoming space for spiritual growth.',
      contact: 'youth@church.org'
    },
    scheduleDetails: [
      {
        day: 'Saturday',
        time: '5:00 PM - 6:00 PM',
        activity: 'Worship & Teaching',
        location: 'Youth Center'
      },
      {
        day: 'Saturday',
        time: '6:00 PM - 7:00 PM',
        activity: 'Small Group Discussions',
        location: 'Youth Center'
      },
      {
        day: 'Saturday',
        time: '7:00 PM - 8:00 PM',
        activity: 'Fellowship & Activities',
        location: 'Youth Center'
      },
      {
        day: 'Monthly',
        time: 'Various',
        activity: 'Service Projects & Outings',
        location: 'Off-site'
      }
    ],
    quickInfo: [
      'Ages 18-35',
      'Casual atmosphere',
      'Dinner provided',
      'New visitors welcome'
    ]
  },
  {
    id: 'family-meeting',
    title: 'Family Meeting',
    icon: 'ri-home-heart-line',
    schedule: 'First Sunday, 12:30 PM',
    location: 'Fellowship Hall',
    description: 'Monthly gatherings for families to strengthen bonds, share meals, and grow together in faith through activities and fellowship.',
    extendedDescription: 'Family Meeting brings together families of all sizes and stages for a time of connection, learning, and fun. Each month features a potluck meal, family-friendly activities, and teaching on topics relevant to Christian family life—parenting, marriage, raising children in the faith, and navigating modern challenges. It\'s a wonderful opportunity for families to build relationships with other families and for children to see their parents actively engaged in church community.',
    features: [
      'Monthly potluck meals',
      'Family-friendly activities',
      'Parenting workshops',
      'Marriage enrichment',
      'Children\'s programs',
      'Multi-generational fellowship'
    ],
    image: '/assets/readdy/events-main-001-9e5bec3e8310.jpg',
    gallery: [
      '/assets/readdy/events-main-001-9e5bec3e8310.jpg',
      '/assets/readdy/events-main-001-9e5bec3e8310.jpg',
      '/assets/readdy/events-main-001-9e5bec3e8310.jpg',
      '/assets/readdy/events-main-001-9e5bec3e8310.jpg',
      '/assets/readdy/events-main-001-9e5bec3e8310.jpg',
      '/assets/readdy/events-main-001-9e5bec3e8310.jpg'
    ],
    testimonials: [
      {
        quote: 'Family Meeting has been such a blessing! Our kids have made friends, and we\'ve connected with other parents who share our values. It\'s become a monthly tradition we all look forward to.',
        name: 'Michael & Lisa Tadros',
        role: 'Parents of three'
      },
      {
        quote: 'The parenting workshops have given us practical tools rooted in faith. We\'ve learned so much and feel supported by this wonderful community.',
        name: 'George & Nadia Rizk',
        role: 'Parents of two'
      }
    ],
    leadership: {
      name: 'Deacon Samuel & Tasoni Irene',
      title: 'Family Ministry Coordinators',
      bio: 'Deacon Samuel and Tasoni Irene are passionate about strengthening families. As parents themselves, they understand the joys and challenges of raising children in today\'s world and create programming that is both practical and spiritually enriching.',
      contact: 'family@church.org'
    },
    scheduleDetails: [
      {
        day: 'First Sunday of Month',
        time: '12:30 PM - 1:30 PM',
        activity: 'Potluck Lunch',
        location: 'Fellowship Hall'
      },
      {
        day: 'First Sunday of Month',
        time: '1:30 PM - 2:30 PM',
        activity: 'Family Activities & Teaching',
        location: 'Fellowship Hall'
      },
      {
        day: 'First Sunday of Month',
        time: '2:30 PM - 3:00 PM',
        activity: 'Fellowship & Free Play',
        location: 'Fellowship Hall & Playground'
      }
    ],
    quickInfo: [
      'All family sizes welcome',
      'Bring a dish to share',
      'Activities for all ages',
      'Childcare available'
    ]
  },
  // Remaining ministries with basic info (to be enhanced in next steps)
  {
    id: 'seniors-meeting',
    title: 'Seniors Meeting',
    icon: 'ri-user-heart-line',
    schedule: 'Thursdays, 10:00 AM',
    location: 'Community Room',
    description: 'A welcoming community for our elder members to fellowship, pray, and enjoy activities together.',
    extendedDescription: 'Seniors Meeting provides a warm and welcoming environment for our elder church members to connect, share life experiences, and grow spiritually together. Each gathering includes devotional time, prayer, engaging discussions on topics relevant to this season of life, and various activities. We celebrate the wisdom and contributions of our senior members while providing opportunities for meaningful fellowship, learning, and service. Transportation assistance is available for those who need it.',
    features: [
      'Weekly fellowship',
      'Prayer and devotions',
      'Guest speakers',
      'Light refreshments',
      'Social activities',
      'Transportation available'
    ],
    image: '/assets/readdy/events-main-001-9e5bec3e8310.jpg',
    gallery: [
      '/assets/readdy/events-main-001-9e5bec3e8310.jpg',
      '/assets/readdy/events-main-001-9e5bec3e8310.jpg',
      '/assets/readdy/events-main-001-9e5bec3e8310.jpg',
      '/assets/readdy/events-main-001-9e5bec3e8310.jpg',
      '/assets/readdy/events-main-001-9e5bec3e8310.jpg',
      '/assets/readdy/events-main-001-9e5bec3e8310.jpg'
    ],
    testimonials: [
      {
        quote: 'After my husband passed, I felt so alone. Seniors Meeting gave me a new family. The friendships I\'ve made here have brought joy back into my life.',
        name: 'Evelyn Boutros',
        role: 'Member since 2018'
      },
      {
        quote: 'I look forward to Thursdays all week! The fellowship, the prayers, and the activities keep me active and connected. It\'s a blessing to be part of this community.',
        name: 'William Habib',
        role: 'Member since 2019'
      }
    ],
    leadership: {
      name: 'Tasoni Magda Youssef',
      title: 'Seniors Ministry Coordinator',
      bio: 'Tasoni Magda has a special heart for our senior members and understands the unique joys and challenges of this life stage. Her compassionate care and creative programming make each gathering meaningful and enjoyable.',
      contact: 'seniors@church.org'
    },
    scheduleDetails: [
      {
        day: 'Thursday',
        time: '10:00 AM - 10:30 AM',
        activity: 'Arrival & Coffee Fellowship',
        location: 'Community Room'
      },
      {
        day: 'Thursday',
        time: '10:30 AM - 11:15 AM',
        activity: 'Devotional & Prayer Time',
        location: 'Community Room'
      },
      {
        day: 'Thursday',
        time: '11:15 AM - 12:00 PM',
        activity: 'Program (Speaker, Activity, or Discussion)',
        location: 'Community Room'
      },
      {
        day: 'Thursday',
        time: '12:00 PM - 12:30 PM',
        activity: 'Light Lunch & Fellowship',
        location: 'Community Room'
      }
    ],
    quickInfo: [
      'Ages 60 and above',
      'Transportation available',
      'Wheelchair accessible',
      'Light lunch provided'
    ]
  },
  {
    id: 'church-choirs',
    title: 'Church Choirs',
    icon: 'ri-music-line',
    schedule: 'Sundays, 8:00 AM',
    location: 'Sanctuary',
    description: 'Join our sacred music ministry and lead the congregation in worship through beautiful hymns and chants.',
    extendedDescription: 'The Church Choirs ministry is the musical heart of our liturgical worship, leading the congregation in sacred hymns and responses during Divine Liturgy and special services. We have multiple choir groups serving different liturgies and occasions, from children\'s choir to adult chanters. Members receive training in Coptic and Arabic hymns, proper liturgical responses, and musical notation. Being part of the choir is both a spiritual discipline and a joyful service, as you help create an atmosphere of reverence and beauty in worship.',
    features: [
      'Multiple choir groups',
      'Weekly rehearsals',
      'Liturgical performances',
      'Special feast celebrations',
      'Voice training',
      'All ages welcome'
    ],
    image: '/assets/readdy/coptic-church-interior-001-fdff2518bcad.jpg',
    gallery: [
      '/assets/readdy/coptic-church-interior-001-fdff2518bcad.jpg',
      '/assets/readdy/coptic-church-interior-001-fdff2518bcad.jpg',
      '/assets/readdy/coptic-church-interior-001-fdff2518bcad.jpg',
      '/assets/readdy/coptic-church-interior-001-fdff2518bcad.jpg',
      '/assets/readdy/coptic-church-interior-001-fdff2518bcad.jpg',
      '/assets/readdy/coptic-church-interior-001-fdff2518bcad.jpg'
    ],
    testimonials: [
      {
        quote: 'Singing in the choir has deepened my prayer life immensely. When I chant the liturgy, I feel truly connected to centuries of Orthodox worship tradition.',
        name: 'Anthony Girgis',
        role: 'Choir Member since 2017'
      },
      {
        quote: 'I was nervous about my singing ability, but the choir welcomed me warmly and provided excellent training. Now I can\'t imagine Sunday without serving in this ministry.',
        name: 'Veronica Nabil',
        role: 'Choir Member since 2020'
      }
    ],
    leadership: {
      name: 'Cantor Bishoy Fawzy',
      title: 'Choir Director',
      bio: 'Cantor Bishoy has led our church choirs for over 12 years, bringing both musical excellence and spiritual depth to our worship. He leads our media team with patience and expertise, ensuring high-quality broadcasts and a strong digital presence for our church.',
      contact: 'media@church.org'
    },
    scheduleDetails: [
      {
        day: 'Sunday',
        time: '7:30 AM - 11:00 AM',
        activity: 'Live Stream Production (Divine Liturgy)',
        location: 'Media Room & Sanctuary'
      },
      {
        day: 'Tuesday',
        time: '6:00 PM - 8:00 PM',
        activity: 'Team Meeting & Training',
        location: 'Media Room'
      },
      {
        day: 'As Needed',
        time: 'Various',
        activity: 'Event Technical Support',
        location: 'Various Locations'
      },
      {
        day: 'Ongoing',
        time: 'Flexible',
        activity: 'Website & Social Media Management',
        location: 'Remote/Media Room'
      }
    ],
    quickInfo: [
      'Tech skills helpful but not required',
      'Training provided',
      'Flexible schedule',
      'Remote opportunities available'
    ]
  },
  {
    id: 'kitchen-services',
    title: 'Kitchen Services',
    icon: 'ri-restaurant-line',
    schedule: 'Various times',
    location: 'Church Kitchen',
    description: 'Prepare and serve meals for fellowship events, supporting our community through hospitality.',
    extendedDescription: 'The Kitchen Services ministry embodies Christian hospitality by preparing and serving meals for church events, fellowship gatherings, and special occasions. This dedicated team ensures that when our community comes together, delicious food and warm hospitality are always part of the experience. From coffee hour after liturgy to large feast day celebrations, kitchen volunteers work behind the scenes to nourish both body and spirit. No culinary expertise required—just a willing heart and a love for serving others.',
    features: [
      'Meal preparation',
      'Event catering',
      'Fellowship meals',
      'Special occasions',
      'Food safety certified',
      'Volunteer opportunities'
    ],
    image: '/assets/readdy/service-kitchen-009-3f8b8638471b.jpg',
    gallery: [
      '/assets/readdy/service-kitchen-009-3f8b8638471b.jpg',
      '/assets/readdy/service-kitchen-009-3f8b8638471b.jpg',
      '/assets/readdy/service-kitchen-009-3f8b8638471b.jpg',
      '/assets/readdy/service-clean-011-3861358a8685.jpg',
      '/assets/readdy/service-kitchen-009-3f8b8638471b.jpg',
      '/assets/readdy/service-kitchen-009-3f8b8638471b.jpg'
    ],
    testimonials: [
      {
        quote: 'Serving in the kitchen has taught me that hospitality is a form of worship. Seeing people enjoy a meal we prepared with love brings such joy to my heart.',
        name: 'Nadia Rizk',
        role: 'Kitchen Volunteer since 2018'
      },
      {
        quote: 'I love being part of this team! We laugh, we work together, and we serve our church family. It\'s hard work but so rewarding. Plus, I\'ve learned some great recipes!',
        name: 'George Tadros',
        role: 'Kitchen Volunteer since 2020'
      }
    ],
    leadership: {
      name: 'Tasoni Samia Kamal',
      title: 'Kitchen Services Coordinator',
      bio: 'Tasoni Samia has a gift for organization and a passion for hospitality. She coordinates our kitchen volunteers with grace and efficiency, ensuring every event is well-fed and every volunteer feels appreciated.',
      contact: 'kitchen@church.org'
    },
    scheduleDetails: [
      {
        day: 'Sunday',
        time: '11:00 AM - 1:00 PM',
        activity: 'Coffee Hour Service',
        location: 'Church Kitchen & Fellowship Hall'
      },
      {
        day: 'First Sunday',
        time: '11:30 AM - 2:00 PM',
        activity: 'Family Meeting Potluck Support',
        location: 'Church Kitchen'
      },
      {
        day: 'Feast Days',
        time: 'Various',
        activity: 'Special Celebration Meals',
        location: 'Church Kitchen'
      },
      {
        day: 'As Needed',
        time: 'Various',
        activity: 'Event Catering',
        location: 'Church Kitchen'
      }
    ],
    quickInfo: [
      'No cooking experience required',
      'Food safety training provided',
      'Flexible commitment',
      'Team environment'
    ]
  },
  {
    id: 'holy-bread-services',
    title: 'Holy Bread Services',
    icon: 'ri-cake-3-line',
    schedule: 'Saturdays, 9:00 AM',
    location: 'Preparation Room',
    description: 'Sacred ministry of preparing the Korban (Holy Bread) for the Divine Liturgy with prayer and devotion.',
    extendedDescription: 'The Holy Bread Services ministry is one of the most sacred and reverent services in our church. Volunteers prepare the Korban (Holy Bread) that will be consecrated during the Divine Liturgy and become the Body of Christ. This preparation is done with prayer, fasting, and spiritual devotion, following ancient traditions passed down through generations. Those who serve in this ministry understand the profound spiritual significance of their work and approach it with the utmost reverence and purity of heart.',
    features: [
      'Korban preparation',
      'Prayer and devotion',
      'Traditional methods',
      'Training provided',
      'Spiritual significance',
      'Weekly service'
    ],
    image: '/assets/readdy/coptic-cross-heritage-001-3c4d60a8df29.jpg',
    gallery: [
      '/assets/readdy/coptic-cross-heritage-001-3c4d60a8df29.jpg',
      '/assets/readdy/coptic-cross-heritage-001-3c4d60a8df29.jpg',
      '/assets/readdy/coptic-cross-heritage-001-3c4d60a8df29.jpg',
      '/assets/readdy/coptic-cross-heritage-001-3c4d60a8df29.jpg',
      '/assets/readdy/coptic-cross-heritage-001-3c4d60a8df29.jpg',
      '/assets/readdy/coptic-cross-heritage-001-3c4d60a8df29.jpg'
    ],
    testimonials: [
      {
        quote: 'Preparing the Holy Bread has transformed my spiritual life. The hours spent in prayer and preparation bring me closer to God in ways I never imagined. It\'s a profound honor.',
        name: 'Tasoni Marian Youssef',
        role: 'Volunteer since 2015'
      },
      {
        quote: 'When I learned the ancient traditions and prayers for preparing the Korban, I felt connected to centuries of faithful servants. This ministry requires dedication, but the spiritual rewards are immeasurable.',
        name: 'Evelyn Salib',
        role: 'Volunteer since 2019'
      }
    ],
    leadership: {
      name: 'Tasoni Hoda Mikhail',
      title: 'Holy Bread Services Coordinator',
      bio: 'Tasoni Hoda has served in this sacred ministry for over 25 years. Her deep spirituality, attention to detail, and knowledge of traditional methods make her an excellent teacher for those called to this reverent service.',
      contact: 'holybread@church.org'
    },
    scheduleDetails: [
      {
        day: 'Saturday',
        time: '9:00 AM - 9:30 AM',
        activity: 'Prayer & Spiritual Preparation',
        location: 'Preparation Room'
      },
      {
        day: 'Saturday',
        time: '9:30 AM - 11:30 AM',
        activity: 'Korban Preparation & Baking',
        location: 'Preparation Room'
      },
      {
        day: 'Saturday',
        time: '11:30 AM - 12:00 PM',
        activity: 'Final Prayers & Blessing',
        location: 'Preparation Room'
      }
    ],
    quickInfo: [
      'Spiritual preparation required',
      'Training provided',
      'Fasting before service',
      'Women\'s ministry'
    ]
  },
  {
    id: 'cleaning-services',
    title: 'Cleaning Services',
    icon: 'ri-brush-line',
    schedule: 'Saturdays, 8:00 AM',
    location: 'Entire Church',
    description: 'Maintain the cleanliness and beauty of God\'s house through dedicated service and care.',
    extendedDescription: 'The Cleaning Services ministry ensures that our church remains a beautiful, welcoming, and well-maintained space for worship and fellowship. Volunteers work together each week to clean the sanctuary, fellowship halls, classrooms, and all church facilities. This humble service is an act of love and devotion, preparing God\'s house to receive His people. Whether vacuuming, dusting, polishing, or organizing, every task is done with care and reverence. This ministry welcomes anyone willing to serve, and the fellowship among volunteers makes the work joyful.',
    features: [
      'Weekly cleaning',
      'Sanctuary care',
      'Facility maintenance',
      'Team coordination',
      'Supplies provided',
      'Flexible schedule'
    ],
    image: '/assets/readdy/service-clean-011-3861358a8685.jpg',
    gallery: [
      '/assets/readdy/service-clean-011-3861358a8685.jpg',
      '/assets/readdy/service-clean-011-3861358a8685.jpg',
      '/assets/readdy/service-clean-011-3861358a8685.jpg',
      '/assets/readdy/service-clean-011-3861358a8685.jpg',
      '/assets/readdy/service-clean-011-3861358a8685.jpg',
      '/assets/readdy/service-clean-011-3861358a8685.jpg'
    ],
    testimonials: [
      {
        quote: 'Cleaning the church may seem like simple work, but it\'s my way of showing love for God\'s house. When I see families worshiping in a clean, beautiful space, my heart is full.',
        name: 'Joseph Hanna',
        role: 'Volunteer since 2017'
      },
      {
        quote: 'I joined the cleaning team after retiring, and it\'s been such a blessing. The fellowship with other volunteers is wonderful, and I love knowing I\'m contributing to our church community.',
        name: 'Margaret Girgis',
        role: 'Volunteer since 2020'
      }
    ],
    leadership: {
      name: 'Deacon Thomas Nabil',
      title: 'Facilities Coordinator',
      bio: 'Deacon Thomas oversees all church maintenance and cleaning operations. His organizational skills and servant\'s heart ensure our facilities are always well-maintained and welcoming for all who enter.',
      contact: 'facilities@church.org'
    },
    scheduleDetails: [
      {
        day: 'Saturday',
        time: '8:00 AM - 9:00 AM',
        activity: 'Sanctuary & Altar Area Cleaning',
        location: 'Sanctuary'
      },
      {
        day: 'Saturday',
        time: '9:00 AM - 10:00 AM',
        activity: 'Classrooms & Offices',
        location: 'Education Wing'
      },
      {
        day: 'Saturday',
        time: '10:00 AM - 11:00 AM',
        activity: 'Fellowship Hall & Kitchen',
        location: 'Fellowship Areas'
      },
      {
        day: 'Saturday',
        time: '11:00 AM - 11:30 AM',
        activity: 'Restrooms & Final Walkthrough',
        location: 'Entire Church'
      }
    ],
    quickInfo: [
      'All ages welcome',
      'Supplies provided',
      'Flexible commitment',
      'Team environment'
    ]
  },
  {
    id: 'homeless-services',
    title: 'Homeless Services',
    icon: 'ri-hand-heart-line',
    schedule: 'Saturdays, 11:00 AM',
    location: 'Community Outreach Center',
    description: 'Serve our community by providing meals, clothing, and support to those experiencing homelessness.',
    extendedDescription: 'The Homeless Services ministry embodies Christ\'s call to serve "the least of these" by providing practical help and compassionate care to individuals and families experiencing homelessness in our community. Every Saturday, volunteers prepare and serve hot meals, distribute clothing and hygiene kits, and offer a listening ear and prayer. Beyond meeting immediate physical needs, this ministry treats every person with dignity and respect, recognizing the image of God in each individual. Volunteers often say they receive more than they give through this transformative service.',
    features: [
      'Hot meal service',
      'Clothing distribution',
      'Hygiene kits',
      'Resource referrals',
      'Compassionate care',
      'Community partnerships'
    ],
    image: '/assets/readdy/service-homeless-012-d56492405544.jpg',
    gallery: [
      '/assets/readdy/service-homeless-012-d56492405544.jpg',
      '/assets/readdy/service-homeless-012-d56492405544.jpg',
      '/assets/readdy/service-homeless-012-d56492405544.jpg',
      '/assets/readdy/service-homeless-012-d56492405544.jpg',
      '/assets/readdy/service-homeless-012-d56492405544.jpg',
      '/assets/readdy/service-homeless-012-d56492405544.jpg'
    ],
    testimonials: [
      {
        quote: 'Serving in homeless outreach has changed my perspective on everything. The gratitude and resilience I see in those we serve humbles me. This ministry has taught me what it truly means to love your neighbor.',
        name: 'David Samir',
        role: 'Volunteer since 2018'
      },
      {
        quote: 'I thought I was going to help others, but I\'ve received so much more than I\'ve given. The stories, the humanity, the hope—this ministry has deepened my faith and opened my heart.',
        name: 'Rachel Mikhail',
        role: 'Volunteer since 2021'
      }
    ],
    leadership: {
      name: 'Deacon Paul Fawzy',
      title: 'Community Outreach Director',
      bio: 'Deacon Paul has dedicated his life to serving the marginalized and vulnerable. His compassionate leadership and partnerships with local organizations have expanded our outreach impact significantly.',
      contact: 'outreach@church.org'
    },
    scheduleDetails: [
      {
        day: 'Saturday',
        time: '9:00 AM - 11:00 AM',
        activity: 'Meal Preparation',
        location: 'Church Kitchen'
      },
      {
        day: 'Saturday',
        time: '11:00 AM - 11:30 AM',
        activity: 'Setup & Prayer',
        location: 'Community Outreach Center'
      },
      {
        day: 'Saturday',
        time: '11:30 AM - 1:30 PM',
        activity: 'Meal Service & Distribution',
        location: 'Community Outreach Center'
      },
      {
        day: 'Saturday',
        time: '1:30 PM - 2:00 PM',
        activity: 'Cleanup & Debrief',
        location: 'Community Outreach Center'
      }
    ],
    quickInfo: [
      'All ages welcome (teens with parent)',
      'Training provided',
      'Safety protocols in place',
      'Donation opportunities'
    ]
  },
  {
    id: 'library-service',
    title: 'Library Service',
    icon: 'ri-book-2-line',
    schedule: 'Sundays, 11:00 AM - 1:00 PM',
    location: 'Church Library',
    description: 'Access spiritual resources, books, and materials to support your faith journey and learning.',
    extendedDescription: 'The Church Library is a treasure trove of spiritual resources designed to support your faith journey and theological education. Our collection includes Orthodox theology, Biblical commentaries, church history, lives of saints, spiritual formation books, children\'s Christian literature, and multimedia resources. Volunteer librarians are available to help you find materials, recommend readings, and provide research assistance. The library also offers a quiet space for study, reflection, and prayer. Whether you\'re preparing for Sunday School teaching, deepening your personal faith, or researching church history, our library is here to serve you.',
    features: [
      'Extensive book collection',
      'Borrowing privileges',
      'Study resources',
      'Quiet reading space',
      'Digital resources',
      'Research assistance'
    ],
    image: '/assets/readdy/coptic-church-interior-001-fdff2518bcad.jpg',
    gallery: [
      '/assets/readdy/coptic-church-interior-001-fdff2518bcad.jpg',
      '/assets/readdy/coptic-church-interior-001-fdff2518bcad.jpg',
      '/assets/readdy/coptic-church-interior-001-fdff2518bcad.jpg',
      '/assets/readdy/coptic-church-interior-001-fdff2518bcad.jpg',
      '/assets/readdy/coptic-church-interior-001-fdff2518bcad.jpg',
      '/assets/readdy/coptic-church-interior-001-fdff2518bcad.jpg'
    ],
    testimonials: [
      {
        quote: 'The church library has been instrumental in my spiritual growth. The librarians always know exactly what book I need, and the quiet space is perfect for study and prayer.',
        name: 'Father Michael Tadros',
        role: 'Regular Patron'
      },
      {
        quote: 'As a Sunday School teacher, I rely on the library\'s resources every week. The children\'s section is wonderful, and I love that I can borrow materials to enhance my lessons.',
        name: 'Christina Salib',
        role: 'Sunday School Teacher'
      }
    ],
    leadership: {
      name: 'Dr. Mina Kamal',
      title: 'Library Director',
      bio: 'Dr. Mina holds a doctorate in Theology and has curated our library collection with care and expertise. His passion for Orthodox literature and commitment to making resources accessible to all make our library a valuable asset to the church community.',
      contact: 'library@church.org'
    },
    scheduleDetails: [
      {
        day: 'Sunday',
        time: '11:00 AM - 1:00 PM',
        activity: 'Library Open Hours',
        location: 'Church Library'
      },
      {
        day: 'Wednesday',
        time: '6:00 PM - 8:00 PM',
        activity: 'Evening Study Hours',
        location: 'Church Library'
      },
      {
        day: 'By Appointment',
        time: 'Flexible',
        activity: 'Research Assistance',
        location: 'Church Library'
      }
    ],
    quickInfo: [
      'Free membership',
      '3-week borrowing period',
      'Quiet study space',
      'Digital catalog available'
    ]
  }
];

export const getAllMinistries = () => {
  return ministries;
};

export const getMinistryById = (id: string) => {
  return ministries.find(ministry => ministry.id === id) || null;
};
